import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Activity, Lock, Mail, UserPlus } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingSystem, setCheckingSystem] = useState(true);
  const [needsInitialAdmin, setNeedsInitialAdmin] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [adminData, setAdminData] = useState({ 
    email: '', 
    password: '', 
    full_name: ''
  });

  // Verifică dacă sistemul are utilizatori
  useEffect(() => {
    const checkSystem = async () => {
      try {
        // Încearcă să obții health check
        await axios.get(`${API_URL}/health`);
        // Încearcă login cu date invalide pentru a vedea dacă există utilizatori
        try {
          await axios.post(`${API_URL}/auth/init-admin`, { 
            email: 'test@test.com', 
            password: 'test',
            full_name: 'Test'
          });
          // Dacă a reușit, sistemul nu avea utilizatori - trebuie să îl ștergem
          // și să afișăm formularul de creare admin
          setNeedsInitialAdmin(true);
        } catch (err) {
          if (err.response?.status === 403) {
            // Sistemul are deja utilizatori
            setNeedsInitialAdmin(false);
          } else {
            setNeedsInitialAdmin(true);
          }
        }
      } catch (error) {
        console.error('System check failed:', error);
      } finally {
        setCheckingSystem(false);
      }
    };
    checkSystem();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Autentificare reușită!');
      navigate('/calendar');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la autentificare');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInitialAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/init-admin`, {
        email: adminData.email,
        password: adminData.password,
        full_name: adminData.full_name,
        role: 'admin'
      });
      toast.success('Cont administrator creat cu succes! Vă puteți autentifica.');
      setNeedsInitialAdmin(false);
      setLoginData({ email: adminData.email, password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la crearea contului');
    } finally {
      setLoading(false);
    }
  };

  if (checkingSystem) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          backgroundImage: 'url(/img_background.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 login-overlay" />
        <div className="relative z-10">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: 'url(/img_background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 login-overlay" />
      
      <div className="relative z-10 w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Activity className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight font-[Manrope]">DiabetoCare</h1>
              <p className="text-slate-300 text-sm">Management Cabinet Diabetologie</p>
            </div>
          </div>
        </div>

        <Card className="border-0 shadow-2xl">
          {needsInitialAdmin ? (
            <>
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-semibold flex items-center justify-center gap-2">
                  <UserPlus className="w-6 h-6 text-emerald-500" />
                  Configurare Inițială
                </CardTitle>
                <CardDescription>
                  Creați contul de administrator pentru a începe
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateInitialAdmin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-name">Nume complet</Label>
                    <Input
                      id="admin-name"
                      type="text"
                      placeholder="Dr. Administrator"
                      value={adminData.full_name}
                      onChange={(e) => setAdminData({ ...adminData, full_name: e.target.value })}
                      required
                      data-testid="admin-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="admin-email"
                        type="email"
                        placeholder="admin@cabinet.ro"
                        className="pl-10"
                        value={adminData.email}
                        onChange={(e) => setAdminData({ ...adminData, email: e.target.value })}
                        required
                        data-testid="admin-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-password">Parolă</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="admin-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={adminData.password}
                        onChange={(e) => setAdminData({ ...adminData, password: e.target.value })}
                        required
                        minLength={6}
                        data-testid="admin-password-input"
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-500 hover:bg-emerald-600" 
                    disabled={loading}
                    data-testid="create-admin-btn"
                  >
                    {loading ? 'Se procesează...' : 'Creează Administrator'}
                  </Button>
                </form>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-semibold">Bine ați venit!</CardTitle>
                <CardDescription>Autentificați-vă pentru a continua</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="doctor@cabinet.ro"
                        className="pl-10"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Parolă</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        data-testid="login-password-input"
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-500 hover:bg-emerald-600" 
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? 'Se procesează...' : 'Autentificare'}
                  </Button>
                </form>
                <p className="text-center text-xs text-slate-500 mt-4">
                  Conturile sunt create de administrator din aplicație
                </p>
              </CardContent>
            </>
          )}
        </Card>
        
        <p className="text-center text-slate-400 text-sm mt-6">
          © 2024 DiabetoCare. Toate drepturile rezervate.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
